import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { getFirestore, collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { EMAIL_TEMPLATES, renderPreviewHtml, applySamplePlaceholders } from '../sections/@dashboard/emailNotifications/templateMeta';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'color', 'list', 'align', 'link'];

function draftFromSettings(meta, settings) {
  const stored = settings || {};
  const useCustom = Boolean(stored.useCustom);
  return {
    subject: useCustom && stored.subject != null ? stored.subject : meta.defaults.subject,
    title: useCustom && stored.title != null ? stored.title : meta.defaults.title,
    intro: useCustom && stored.intro != null ? stored.intro : meta.defaults.intro,
    body: useCustom && stored.body != null ? stored.body : meta.defaults.body,
    footerNote: useCustom && stored.footerNote != null ? stored.footerNote : meta.defaults.footerNote,
  };
}

export default function EmailNotificationsPage() {
  const [settingsMap, setSettingsMap] = useState({}); // key -> doc data de Firestore
  const [selectedKey, setSelectedKey] = useState(EMAIL_TEMPLATES[0].key);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const quillRef = useRef(null);

  const selectedMeta = useMemo(
    () => EMAIL_TEMPLATES.find((t) => t.key === selectedKey),
    [selectedKey]
  );
  const selectedSettings = settingsMap[selectedKey];
  const isEnabled = !selectedSettings || selectedSettings.enabled !== false;
  const isCustom = Boolean(selectedSettings && selectedSettings.useCustom);

  useEffect(() => {
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, 'emailTemplates'));
        const map = {};
        snap.forEach((d) => {
          map[d.id] = d.data();
        });
        setSettingsMap(map);
      } catch (e) {
        console.error(e);
        setMessage({ type: 'error', text: 'Error cargando las plantillas guardadas.' });
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (loading || !selectedMeta) return;
    setDraft(draftFromSettings(selectedMeta, settingsMap[selectedMeta.key]));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedKey]);

  const handleSelect = (key) => {
    if (key === selectedKey) return;
    if (dirty && !window.confirm('Tienes cambios sin guardar. ¿Descartarlos?')) return;
    setMessage(null);
    setSelectedKey(key);
  };

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const persist = async (key, data) => {
    const db = getFirestore();
    await setDoc(doc(db, 'emailTemplates', key), { ...data, updatedAt: new Date() }, { merge: true });
    setSettingsMap((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...data } }));
  };

  const handleToggleEnabled = async (key, enabled) => {
    try {
      await persist(key, { enabled });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Error actualizando la notificación.' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await persist(selectedKey, { ...draft, useCustom: true });
      setDirty(false);
      setMessage({ type: 'success', text: `"${selectedMeta.name}" guardada. Los próximos emails usarán este contenido.` });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Error guardando la plantilla.' });
    }
    setSaving(false);
  };

  const handleRestoreDefault = async () => {
    if (!window.confirm('¿Restaurar el diseño predeterminado? Tu versión personalizada dejará de usarse.')) return;
    setSaving(true);
    setMessage(null);
    try {
      await persist(selectedKey, { useCustom: false });
      setDraft(draftFromSettings(selectedMeta, null));
      setDirty(false);
      setMessage({ type: 'success', text: 'Se restauró el diseño predeterminado.' });
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Error restaurando la plantilla.' });
    }
    setSaving(false);
  };

  const insertPlaceholder = (token) => {
    const editor = quillRef.current && quillRef.current.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, token, 'user');
    editor.setSelection(index + token.length, 0);
  };

  if (loading || !draft) {
    return (
      <Container>
        <Stack alignItems="center" sx={{ mt: 10 }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Email Notifications | BBTC</title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 1 }}>
          Notificaciones de Email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Activa o desactiva cada notificación y personaliza su contenido. Usa los placeholders para insertar datos
          dinámicos (nombre del cliente, fecha, montos, etc.).
        </Typography>

        {message && (
          <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Lista de notificaciones */}
          <Grid item xs={12} md={4}>
            <Card>
              <List disablePadding>
                {EMAIL_TEMPLATES.map((tpl, i) => {
                  const stored = settingsMap[tpl.key];
                  const enabled = !stored || stored.enabled !== false;
                  const custom = Boolean(stored && stored.useCustom);
                  return (
                    <Box key={tpl.key}>
                      {i > 0 && <Divider />}
                      <ListItemButton
                        selected={tpl.key === selectedKey}
                        onClick={() => handleSelect(tpl.key)}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Tooltip title={enabled ? 'Notificación habilitada' : 'Notificación deshabilitada'}>
                            <Checkbox
                              edge="start"
                              checked={enabled}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleToggleEnabled(tpl.key, e.target.checked)}
                            />
                          </Tooltip>
                        </ListItemIcon>
                        <ListItemText
                          primary={tpl.name}
                          secondary={tpl.recipient === 'Staff' ? 'Para el equipo' : 'Para el cliente'}
                          primaryTypographyProps={{ fontWeight: tpl.key === selectedKey ? 700 : 500 }}
                        />
                        {custom && <Chip label="Editada" size="small" color="primary" variant="outlined" />}
                      </ListItemButton>
                    </Box>
                  );
                })}
              </List>
            </Card>
          </Grid>

          {/* Editor */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="h6">{selectedMeta.name}</Typography>
                    <Chip
                      size="small"
                      label={isCustom ? 'Personalizada' : 'Predeterminada'}
                      color={isCustom ? 'primary' : 'default'}
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isEnabled}
                        onChange={(e) => handleToggleEnabled(selectedKey, e.target.checked)}
                      />
                    }
                    label="Habilitada"
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {selectedMeta.description}
                </Typography>

                <Stack spacing={2.5}>
                  <TextField
                    label="Asunto (Subject)"
                    value={draft.subject}
                    onChange={(e) => updateDraft('subject', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Título del encabezado"
                    value={draft.title}
                    onChange={(e) => updateDraft('title', e.target.value)}
                    helperText="Texto grande que aparece en la franja roja del email."
                    fullWidth
                  />
                  <TextField
                    label="Introducción"
                    value={draft.intro}
                    onChange={(e) => updateDraft('intro', e.target.value)}
                    multiline
                    minRows={2}
                    helperText="Párrafo debajo del título, dentro de la franja roja."
                    fullWidth
                  />

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Placeholders — haz clic para insertarlos en el mensaje
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {selectedMeta.placeholders.map((p) => (
                        <Tooltip key={p.token} title={p.label}>
                          <Chip
                            label={p.token}
                            size="small"
                            variant="outlined"
                            onClick={() => insertPlaceholder(p.token)}
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      '& .ql-container': { minHeight: 220, fontSize: 14 },
                      '& .ql-editor': { minHeight: 220 },
                      '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
                      '& .ql-container.ql-snow': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Mensaje
                    </Typography>
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={draft.body}
                      onChange={(html) => updateDraft('body', html)}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                    />
                  </Box>

                  <TextField
                    label="Nota al pie"
                    value={draft.footerNote}
                    onChange={(e) => updateDraft('footerNote', e.target.value)}
                    helperText="Texto pequeño al final de la tarjeta del email. Déjalo vacío para omitirlo."
                    fullWidth
                  />

                  <Divider />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Guardando...' : 'Guardar cambios'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setPreviewOpen(true)}
                      >
                        Vista previa
                      </Button>
                    </Stack>
                    {isCustom && (
                      <Button
                        color="error"
                        startIcon={<RestartAltIcon />}
                        onClick={handleRestoreDefault}
                        disabled={saving}
                      >
                        Restaurar predeterminado
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Vista previa */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Vista previa — {selectedMeta.name}
          <Typography variant="body2" color="text.secondary">
            Asunto: {draft && applySamplePlaceholders(selectedKey, draft.subject)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#F4F6F8' }}>
          {previewOpen && draft && (
            <iframe
              title="email-preview"
              srcDoc={renderPreviewHtml(selectedKey, draft)}
              style={{ width: '100%', height: '65vh', border: 0, display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
